import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Checkbox form control that integrates with Angular forms via ControlValueAccessor.
 */
@Component({
  selector: 'app-field-checkbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldCheckboxComponent),
      multi: true,
    },
  ],
  templateUrl: './field-checkbox.component.html',
  styleUrl: './field-checkbox.component.css',
})
export class FieldCheckboxComponent implements ControlValueAccessor {
  /** Checkbox label shown beside the input. */
  readonly label      = input('');
  /** Native id assigned to the checkbox element. */
  readonly id         = input('');

  /** Current checked state. */
  readonly checked    = signal(false);
  /** Disabled state received from Angular forms. */
  readonly isDisabled = signal(false);

  /** Registered Angular forms change callback. */
  private onChangeFn:  (v: boolean) => void = () => {};
  /** Registered Angular forms touched callback. */
  private onTouchedFn: () => void           = () => {};

  /** @inheritdoc */
  writeValue(v: boolean): void              { this.checked.set(!!v); }
  /** @inheritdoc */
  registerOnChange(fn: (v: boolean) => void): void { this.onChangeFn = fn; }
  /** @inheritdoc */
  registerOnTouched(fn: () => void): void          { this.onTouchedFn = fn; }
  /** @inheritdoc */
  setDisabledState(d: boolean): void               { this.isDisabled.set(d); }

  /**
   * Handles native checkbox changes and forwards them to Angular forms.
   *
   * @param event Native change event from the checkbox.
   */
  onChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checked.set(checked);
    this.onChangeFn(checked);
    this.onTouchedFn();
  }
}
